import React, { useEffect, useRef, useState } from "react";
import "./Contact.css";
// Mobile videos (720p, CRF 28)
import VID1WebmMobile from "../../assets/VID1_mobile.webm";
import VID1Mp4Mobile from "../../assets/VID1_mobile.mp4";

// Desktop videos (original quality)
import VID1WebmDesktop from "../../assets/VID1_desktop.webm";
import VID1Mp4Desktop from "../../assets/VID1_desktop.mp4";

import ClickHereButton from "../../assets/Clickherebutton.png";
import { useTranslation } from "react-i18next";



async function submitContactForm(form) {
  try {
    const response = await fetch("https://security-backend-1nbc.onrender.com/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: form.email,
        phone: form.phone,
        first_name: form.firstName,
        last_name: form.lastName,
        company: form.company,
        country: form.country,
        state: form.state,
        city: form.city,
        service: form.need,
        duration: form.duration,
        additional_info: form.details,
        updates_opt_in: form.updatesOptIn,
        consent: form.consent,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to submit form");
    }

    const data = await response.json();
    console.log("Form submitted successfully:", data);
    return data;
  } catch (error) {
    console.error("Error submitting form:", error);
    throw error;
  }
}






function useReveal(opts = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    if (!("IntersectionObserver" in window)) { setInView(true); return; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); io.disconnect(); }
    }, opts);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, opts]);
  return [ref, inView];
}

export default function ContactUs() {
  const { t } = useTranslation();
  const [leftRef, leftIn] = useReveal();
  const [formRef, formIn] = useReveal();

  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  // Detect if viewport is mobile (< 768px)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Select video sources based on viewport
  const videoWebm = isMobile ? VID1WebmMobile : VID1WebmDesktop;
  const videoMp4 = isMobile ? VID1Mp4Mobile : VID1Mp4Desktop;

  // <-- Add INITIAL_FORM here
  const INITIAL_FORM = {
    email: "", phone: "", firstName: "", lastName: "", company: "",
    country: "United States", state: "", city: "", need: "",
    duration: "", details: "", updatesOptIn: false, consent: false,
  };

   const [form, setForm] = useState(INITIAL_FORM);

  function update(name, value) { setForm((f) => ({ ...f, [name]: value })); }

  function handlePlayVideo() {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  }

  function validate() {
    const e = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("contact.form.emailError");
    if (!form.firstName) e.firstName = t("contact.form.required");
    if (!form.lastName) e.lastName = t("contact.form.required");
    if (!form.company) e.company = t("contact.form.required");
    if (!form.country) e.country = t("contact.form.required");
    if (!form.state) e.state = t("contact.form.required");
    if (!form.need) e.need = t("contact.form.needError");
    if (!form.consent) e.consent = t("contact.form.consentError");
    return e;
  }

  async function onSubmit(e) {
    e.preventDefault();
    const eObj = validate(); setErrors(eObj);
    if (Object.keys(eObj).length) return;
    try {
      setStatus("loading");
      await submitContactForm(form); // send to backend on port 5030
      
      // Fire Google Ads conversion event
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11469125087/YSYFCNjew6IaEN_r89wq',
          'value': 1.0,
          'currency': 'CAD'
        });
      }
      
      setStatus("success");
      setForm(INITIAL_FORM);
      setErrors({});
    } catch { setStatus("error"); }
  }

  return (
    <section className="contact">
      <div className="contact__grid">
        <div ref={leftRef} className={`contact__left contact__reveal contact__reveal--left ${leftIn ? "in" : ""}`}>
          <p className="contact__eyebrow">{t("contact.eyebrow")}</p>
          <h1 className="contact__headline">
            {t("contact.headline.part1")} <span className="accent">{t("contact.headline.accent1")}</span>{" "}
            <span className="accent">{t("contact.headline.accent2")}</span> {t("contact.headline.part2")}
          </h1>
          <p className="contact__intro">
            {t("contact.intro")}
          </p>
          <div className="contact__photo">
            {!isPlaying && (
              <button className="contact__play-btn" onClick={handlePlayVideo}>
                <img src={ClickHereButton} alt={t("contact.playAlt")} />
              </button>
            )}
            <video ref={videoRef} loop muted playsInline key={isMobile ? 'mobile' : 'desktop'}>
              <source src={videoWebm} type="video/webm" />
              <source src={videoMp4} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

        <aside
          ref={formRef}
          className={`contact__panel contact__reveal contact__reveal--right ${formIn ? "in" : ""}`}
          aria-label={t("contact.panelTitle")}
        >
          <h2 className="panel__title">{t("contact.panelTitle")}</h2>

          <form className="contact__form" onSubmit={onSubmit} noValidate>
            <div className="contact__field">
              <label htmlFor="email" className="contact__label">{t("contact.form.emailLabel")}</label>
              <input
                id="email" type="email" className="contact__input"
                value={form.email} onChange={(e) => update("email", e.target.value)}
                placeholder={t("contact.form.emailPlaceholder")} required aria-invalid={!!errors.email}
              />
              {errors.email && <span className="contact__err">{errors.email}</span>}
            </div>

            <div className="contact__field">
              <label htmlFor="phone" className="contact__label">{t("contact.form.phoneLabel")}</label>
              <input
                id="phone" type="tel" className="contact__input"
                value={form.phone} onChange={(e) => update("phone", e.target.value)}
                placeholder={t("contact.form.phonePlaceholder")}
              />
            </div>

            <div className="contact__field">
              <label htmlFor="first" className="contact__label">{t("contact.form.firstNameLabel")}</label>
              <input
                id="first" className="contact__input" value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                placeholder={t("contact.form.firstNamePlaceholder")} required aria-invalid={!!errors.firstName}
              />
              {errors.firstName && <span className="contact__err">{errors.firstName}</span>}
            </div>

            <div className="contact__field">
              <label htmlFor="last" className="contact__label">{t("contact.form.lastNameLabel")}</label>
              <input
                id="last" className="contact__input" value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                placeholder={t("contact.form.lastNamePlaceholder")} required aria-invalid={!!errors.lastName}
              />
              {errors.lastName && <span className="contact__err">{errors.lastName}</span>}
            </div>

            <div className="contact__field contact__field--full">
              <label htmlFor="company" className="contact__label">{t("contact.form.companyLabel")}</label>
              <input
                id="company" className="contact__input" value={form.company}
                onChange={(e) => update("company", e.target.value)}
                placeholder={t("contact.form.companyPlaceholder")} required aria-invalid={!!errors.company}
              />
              {errors.company && <span className="contact__err">{errors.company}</span>}
            </div>

            <div className="contact__field">
              <label htmlFor="country" className="contact__label">{t("contact.form.countryLabel")}</label>
              <select
                id="country" className="contact__select" value={form.country}
                onChange={(e) => update("country", e.target.value)} required aria-invalid={!!errors.country}
              >
                <option>{t("contact.form.countries.us")}</option>
                <option>{t("contact.form.countries.ca")}</option>
                <option>{t("contact.form.countries.mx")}</option>
                <option>{t("contact.form.countries.other")}</option>
              </select>
              {errors.country && <span className="contact__err">{errors.country}</span>}
            </div>

            <div className="contact__field">
              <label htmlFor="state" className="contact__label">{t("contact.form.stateLabel")}</label>
              <input
                id="state" className="contact__input" value={form.state}
                onChange={(e) => update("state", e.target.value)}
                placeholder={t("contact.form.statePlaceholder")} required aria-invalid={!!errors.state}
              />
              {errors.state && <span className="contact__err">{errors.state}</span>}
            </div>

            <div className="contact__field contact__field--full">
              <label htmlFor="city" className="contact__label">{t("contact.form.cityLabel")}</label>
              <input
                id="city" className="contact__input" value={form.city}
                onChange={(e) => update("city", e.target.value)} placeholder={t("contact.form.cityPlaceholder")}
              />
            </div>

            <div className="contact__field contact__field--full">
              <label htmlFor="need" className="contact__label">{t("contact.form.needLabel")}</label>
              <select
                id="need" className="contact__select" value={form.need}
                onChange={(e) => update("need", e.target.value)} required aria-invalid={!!errors.need}
              >
                <option value="">{t("contact.form.services.select")}</option>
                <option>{t("contact.form.services.guards")}</option>
                <option>{t("contact.form.services.patrol")}</option>
                <option>{t("contact.form.services.event")}</option>
                <option>{t("contact.form.services.cctv")}</option>
                <option>{t("contact.form.services.risk")}</option>
                <option>{t("contact.form.services.other")}</option>
              </select>
              {errors.need && <span className="contact__err">{errors.need}</span>}
            </div>

            <div className="contact__field contact__field--full">
              <label htmlFor="duration" className="contact__label">{t("contact.form.durations.select")}</label>
              <select
                id="duration" className="contact__select" value={form.duration}
                onChange={(e) => update("duration", e.target.value)}
              >
                <option value="">{t("contact.form.durations.select")}</option>
                <option>{t("contact.form.durations.day")}</option>
                <option>{t("contact.form.durations.days")}</option>
                <option>{t("contact.form.durations.weeks")}</option>
                <option>{t("contact.form.durations.ongoing")}</option>
              </select>
            </div>

            <div className="contact__field contact__field--full">
              <label htmlFor="details" className="contact__label">
                {t("contact.form.detailsLabel")}
              </label>
              <textarea
                id="details" rows={4} className="contact__textarea"
                value={form.details} onChange={(e) => update("details", e.target.value)}
                placeholder={t("contact.form.detailsPlaceholder")}
              />
            </div>

            <label className="contact__check">
              <input
                className="contact__check-input"
                type="checkbox" checked={form.updatesOptIn}
                onChange={(e) => update("updatesOptIn", e.target.checked)}
              />
              <span>{t("contact.form.updatesOptIn")}</span>
            </label>

            <label className="contact__check">
              <input
                className="contact__check-input"
                type="checkbox" checked={form.consent}
                onChange={(e) => update("consent", e.target.checked)} aria-invalid={!!errors.consent}
              />
              <span>
                {t("contact.form.consent")}{" "}
                <a href="/privacy">{t("contact.form.privacyPolicy")}</a>. *
              </span>
            </label>
            {errors.consent && <span className="contact__err">{errors.consent}</span>}

            <div className="contact__actions">
              <button type="submit" className="contact__button" disabled={status === "loading"}>
                {status === "loading" ? t("contact.form.submitting") : t("contact.form.submit")}
              </button>

              {status === "success" && <p className="contact__note contact__note--success" role="status">
                {t("contact.form.success")}
              </p>}
              {status === "error" && <p className="contact__note contact__note--error" role="alert">
                {t("contact.form.error")}
              </p>}
            </div>
          </form>
        </aside>
      </div>
    </section>
  );
}
